import React, { useState, useEffect, useCallback } from "react";
import { Flame } from "lucide-react";
import AvatarPreview from "./AvatarPreview";
import {
  LEAGUE_TIERS,
  PROMOTION_SLOTS,
  DEMOTION_SLOTS,
  calcMomentum,
} from "../lib/leagueUtils";
import {
  applyWeeklyResetIfNeeded,
  loadLeaderboardData,
  updateLeagueOptOut,
} from "../lib/database";

// ── Medical rank titles (mirrors Dashboard.js) ───────────────────────────────
const STREAK_RANKS = [
  { days: 0,   title: "Pre-Med Student",         emoji: "🎓" },
  { days: 10,  title: "MS1 — First Year",         emoji: "📚" },
  { days: 20,  title: "MS2 — Second Year",        emoji: "📖" },
  { days: 35,  title: "MS3 — Clinical Rotations", emoji: "🩺" },
  { days: 50,  title: "MS4 — Senior Student",     emoji: "📋" },
  { days: 65,  title: "Medical Graduate",          emoji: "🎓" },
  { days: 80,  title: "Intern",                   emoji: "🏥" },
  { days: 110, title: "Junior Resident",           emoji: "💊" },
  { days: 140, title: "Senior Resident",           emoji: "🔬" },
  { days: 170, title: "Chief Resident",            emoji: "⚕️" },
  { days: 210, title: "Clinical Fellow",           emoji: "🧬" },
  { days: 280, title: "Attending Physician",       emoji: "👨‍⚕️" },
  { days: 365, title: "Specialist",               emoji: "⭐" },
  { days: 500, title: "Consultant",               emoji: "🌟" },
  { days: 730, title: "Professor of Medicine",    emoji: "👑" },
];

const getMedicalRank = (streak) => {
  let rank = STREAK_RANKS[0];
  for (const r of STREAK_RANKS) {
    if (streak >= r.days) rank = r;
    else break;
  }
  return rank;
};

// ── Podium helpers ────────────────────────────────────────────────────────────
const PODIUM_RING   = { 1: "ring-yellow-400", 2: "ring-gray-400",   3: "ring-orange-400" };
const PODIUM_BLOCK  = { 1: "bg-yellow-400",   2: "bg-gray-300",     3: "bg-orange-400"   };
const PODIUM_MEDAL  = { 1: "🥇",              2: "🥈",              3: "🥉"              };
const PODIUM_HEIGHT = { 1: "h-20",            2: "h-14",            3: "h-10"            };
const PODIUM_OFFSET = { 1: "mt-0",            2: "mt-8",            3: "mt-14"           };

// ── Component ─────────────────────────────────────────────────────────────────
const Leaderboard = ({ onBack, userData, userId, onUserDataChange }) => {
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [group, setGroup]               = useState([]);
  const [myTier, setMyTier]             = useState(userData?.leagueTier || 1);
  const [leagueChange, setLeagueChange] = useState(0);
  const [optOut, setOptOut]             = useState(userData?.leagueOptOut || false);
  const [sortMode, setSortMode]         = useState("weekly");

  const tier = LEAGUE_TIERS[myTier - 1] || LEAGUE_TIERS[0];

  const fetchGroup = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Apply weekly reset / group assignment if needed
      const resetResult = await applyWeeklyResetIfNeeded(userId, {
        weeklyXpResetAt: userData?.weeklyXpResetAt,
        leagueTier:      userData?.leagueTier || 1,
        leagueGroupId:   userData?.leagueGroupId ?? null,
      });

      if (resetResult.changed) {
        setLeagueChange(resetResult.leagueChange);
        setMyTier(resetResult.newTier);
        // Propagate new league state back to App.js so it persists
        if (onUserDataChange) {
          onUserDataChange(prev => ({
            ...prev,
            weeklyXP: 0,
            weeklyXpResetAt: null, // will be re-read on next load
            leagueTier: resetResult.newTier,
            leagueGroupId: resetResult.newGroupId,
          }));
        }
      }

      // 2. Load the group
      const { group: fetchedGroup, myTier: fetchedTier } = await loadLeaderboardData(userId);
      setGroup(fetchedGroup);
      setMyTier(fetchedTier);
    } catch (err) {
      console.error("Leaderboard fetch error:", err);
      setError("Couldn't load the leaderboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userId, userData?.weeklyXpResetAt, userData?.leagueTier, userData?.leagueGroupId, onUserDataChange]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // ── Sort + rank ─────────────────────────────────────────────────────────────
  const getScore = (u) =>
    sortMode === "weekly" ? calcMomentum(u.weeklyXP, u.streak) : u.xp;

  const sorted = [...group]
    .sort((a, b) => getScore(b) - getScore(a))
    .map((u, i) => ({ ...u, rank: i + 1 }));

  const groupSize = sorted.length;
  const top3      = sorted.slice(0, Math.min(3, groupSize));
  const rest      = sorted.slice(3);
  const youEntry  = sorted.find(u => u.isYou);
  const above     = youEntry && youEntry.rank > 1 ? sorted[youEntry.rank - 2] : null;
  const scoreDiff = above ? getScore(above) - getScore(youEntry) : 0;

  const podiumOrder =
    top3.length === 3 ? [top3[1], top3[0], top3[2]] :
    top3.length === 2 ? [top3[1], top3[0]] : top3;

  const isPromoZone = (rank) => rank <= Math.min(PROMOTION_SLOTS, groupSize);
  const isDangerZone = (rank) => groupSize >= 6 && rank > groupSize - DEMOTION_SLOTS;

  const rowBg = (u) => {
    if (u.isYou)                  return "bg-violet-50 border-2 border-violet-400";
    if (isPromoZone(u.rank))      return "bg-green-50 border border-green-200";
    if (isDangerZone(u.rank))     return "bg-red-50 border border-red-200";
    return "bg-gray-50 border border-gray-100";
  };

  // ── Opt-out handler ─────────────────────────────────────────────────────────
  const handleOptOut = async (e) => {
    const val = e.target.checked;
    setOptOut(val);
    if (onUserDataChange) onUserDataChange(prev => ({ ...prev, leagueOptOut: val }));
    await updateLeagueOptOut(userId, val);
    await fetchGroup();
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={onBack} className="mb-4 text-white hover:text-gray-200">
          ← Back to Dashboard
        </button>
        <div className="bg-white rounded-xl p-10 text-center text-gray-500 shadow-sm border border-gray-200">
          <div className="text-2xl mb-2">⏳</div>
          Loading your league…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={onBack} className="mb-4 text-white hover:text-gray-200">
          ← Back to Dashboard
        </button>
        <div className="bg-white rounded-xl p-8 text-center text-red-600 shadow-sm border border-red-200">
          {error}
          <button onClick={fetchGroup} className="block mx-auto mt-4 text-sm text-violet-600 underline">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button onClick={onBack} className="mb-4 text-white hover:text-gray-200">
        ← Back to Dashboard
      </button>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">

        {/* ── League badge header ── */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Leaderboard</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {groupSize} player{groupSize !== 1 ? "s" : ""} in your group this week
            </p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${tier.bg} ${tier.text}`}>
            <span className="text-lg">{tier.emoji}</span>
            <span className="font-bold text-sm">{tier.name} League</span>
          </div>
        </div>

        {/* ── League change banner ── */}
        {leagueChange === 1 && (
          <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-300 rounded-lg px-4 py-2.5 text-green-800 text-sm font-semibold">
            🎉 You were promoted to <span className="font-bold">{tier.name} League</span>!
          </div>
        )}
        {leagueChange === -1 && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-red-700 text-sm">
            📉 You dropped to <span className="font-bold">{tier.name} League</span> — fight back this week!
          </div>
        )}

        {/* ── Sort tabs ── */}
        <div className="flex gap-2 mb-3">
          {[
            { key: "weekly",  label: "⚡ Weekly Momentum" },
            { key: "alltime", label: "🏆 All-Time XP" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSortMode(key)}
              className={
                "px-4 py-2 rounded-lg font-medium text-sm transition-colors " +
                (sortMode === key
                  ? "bg-violet-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200")
              }
            >
              {label}
            </button>
          ))}
        </div>

        {/* Momentum explanation */}
        {sortMode === "weekly" && (
          <div className="mb-4 text-xs text-gray-600 bg-violet-50 rounded-lg px-3 py-2 border border-violet-100">
            ⚡ <span className="font-semibold">Momentum</span> = Weekly XP × (1 + streak ÷ 10). Resets every Monday.
          </div>
        )}

        {/* ── Opt-out toggle ── */}
        <div className="flex items-center justify-end mb-5">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={optOut}
              onChange={handleOptOut}
              className="rounded"
            />
            Hide me from leaderboards
          </label>
        </div>

        {/* ── Empty state ── */}
        {groupSize === 0 && (
          <div className="text-center py-10 text-gray-500">
            <div className="text-3xl mb-2">🏥</div>
            <p className="font-medium">You're the first in your group!</p>
            <p className="text-xs mt-1">More players will join as the app grows.</p>
          </div>
        )}

        {/* ── Podium (top 3) ── */}
        {top3.length > 0 && (
          <div className="flex items-start justify-center gap-6 mb-8 pt-2">
            {podiumOrder.map((u) => {
              const medRank = getMedicalRank(u.streak);
              return (
                <div key={u.id} className={`flex flex-col items-center ${PODIUM_OFFSET[u.rank]}`}>
                  {u.isYou && (
                    <span className="text-xs font-bold text-violet-600 mb-1">YOU</span>
                  )}
                  <div className={`ring-4 ${PODIUM_RING[u.rank]} rounded-full mb-1`}>
                    <AvatarPreview avatar={u.avatar} size="sm" />
                  </div>
                  <p className="text-xs font-bold text-gray-800 text-center max-w-[80px] truncate leading-tight">
                    {u.name}
                  </p>
                  <p className="text-[10px] text-gray-500 text-center leading-tight mb-1">
                    {medRank.emoji} {medRank.title}
                  </p>
                  <div className="flex items-center gap-0.5 mb-2">
                    <Flame className="w-3 h-3 text-orange-500" />
                    <span className="text-[10px] font-semibold text-orange-600">{u.streak}</span>
                  </div>
                  <div className={`${PODIUM_BLOCK[u.rank]} ${PODIUM_HEIGHT[u.rank]} w-20 rounded-t-lg flex flex-col items-center justify-center gap-0.5`}>
                    <span className="text-xl leading-none">{PODIUM_MEDAL[u.rank]}</span>
                    <span className="text-xs font-bold text-white">{getScore(u)}</span>
                    <span className="text-[9px] text-white/80">{sortMode === "weekly" ? "pts" : "XP"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Near-you banner ── */}
        {youEntry?.rank === 1 && (
          <div className="mb-4 flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 text-sm text-yellow-800">
            🥇 You're in <span className="font-bold ml-1">1st place</span> — defend your lead!
          </div>
        )}
        {above && scoreDiff > 0 && (
          <div className="mb-4 flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-4 py-2 text-sm text-teal-800">
            🎯 You're{" "}
            <span className="font-bold mx-1">{scoreDiff} {sortMode === "weekly" ? "momentum pts" : "XP"}</span>{" "}
            behind <span className="font-bold ml-1">#{above.rank} {above.name}</span> — keep going!
          </div>
        )}

        {/* ── Ranked list (rank 4+) ── */}
        {rest.length > 0 && (
          <div className="space-y-2">
            {rest.map(u => {
              const medRank = getMedicalRank(u.streak);
              return (
                <div key={u.id} className={`flex items-center justify-between p-3 rounded-lg ${rowBg(u)}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {u.rank}
                    </div>
                    <AvatarPreview avatar={u.avatar} size="sm" />
                    <div>
                      <div className="font-semibold text-gray-800 text-sm flex items-center gap-1">
                        {u.name}
                        {u.isYou && <span className="text-xs text-violet-600 font-bold">YOU</span>}
                      </div>
                      <div className="text-xs text-gray-500">
                        {medRank.emoji} {medRank.title}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Flame className="w-3 h-3 text-orange-500" />
                      <span className="text-xs font-semibold text-orange-600">{u.streak}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-violet-600 text-sm">{getScore(u)}</div>
                      <div className="text-[10px] text-gray-400">
                        {sortMode === "weekly" ? "momentum" : "XP"}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Zone legend ── */}
        {groupSize >= 6 && (
          <div className="flex items-center gap-4 mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-green-200" />
              Top {Math.min(PROMOTION_SLOTS, groupSize)} → promoted next week
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-red-200" />
              Bottom {Math.min(DEMOTION_SLOTS, groupSize)} → demoted next week
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
